import { Stack } from '@mui/material';
import AssetLogo from 'sections/AssetLogo';
import { useMemo } from 'react';
import { latestExternalConnectionByType } from './latestExternalConnectionByType';
import { ExternalServiceType, MerchantCashAdvance, SyndicationDetailsDto } from '../graphql/types';
import { SizeProps } from '../types/extended';
import CustomTooltip from 'components/CustomTooltip';
import { useExternalServiceLogo } from './externalServiceLogo';
import Avatar from 'components/@extended/Avatar';

interface ExternalConnectionCompanyLogoProps {
  mcaData: MerchantCashAdvance | SyndicationDetailsDto;
  size?: SizeProps;
  mt?: number;
}

const ExternalConnectionCompanyLogo = ({ mcaData, size = 'sm', mt = 0.5, ...props }: ExternalConnectionCompanyLogoProps) => {
  const connection = latestExternalConnectionByType(mcaData, [ExternalServiceType.SimplicityCollect]);
  const serviceConfig = connection?.externalServiceConfiguration;
  const serviceConfigLogo = useExternalServiceLogo(serviceConfig);

  const {
    serviceLogo,
    serviceName,
    companyLogo,
    companyName,
    legalCompanyLogo,
    legalCompanyName,
    externalServiceLogo,
    externalServiceName
  } = useMemo(() => {
    if (!mcaData) {
      return {
        serviceLogo: undefined,
        serviceName: 'Unknown Service',
        companyLogo: undefined,
        companyName: 'UNKNOWN',
        legalCompanyLogo: undefined,
        legalCompanyName: undefined,
        externalServiceLogo: undefined,
        externalServiceName: undefined
      };
    }

    let serviceLogo: string | undefined | null;
    let serviceName: string = 'Unknown Service';
    let companyLogo: string | undefined | null;
    let companyName: string = 'UNKNOWN';
    let legalCompanyLogo: string | undefined | null;
    let legalCompanyName: string = 'UNKNOWN';
    let externalServiceLogo: string | undefined | null;
    let externalServiceName: string | undefined;

    if ('legal' in mcaData && mcaData.legal?.company) {
      legalCompanyLogo = mcaData.legal.company.logo;
      legalCompanyName = mcaData.legal.company.legalName || 'UNKNOWN';
    }
    if (mcaData.integration) {
      serviceLogo = mcaData.integration.logo;
      serviceName = mcaData.integration.name || 'Unknown Service';
      if (mcaData.integration.company) {
        companyLogo = mcaData.integration.company.logo;
        companyName = mcaData.integration.company.legalName || 'UNKNOWN';
      }
    }
    if (serviceConfig?.type === ExternalServiceType.SimplicityCollect) {
      externalServiceLogo = serviceConfigLogo;
      externalServiceName = serviceConfig.name;
    }

    return {
      serviceLogo,
      serviceName,
      companyLogo,
      companyName,
      legalCompanyLogo,
      legalCompanyName,
      externalServiceLogo,
      externalServiceName
    };
  }, [mcaData]);

  if (!mcaData || (!serviceLogo && !companyLogo && !externalServiceLogo && !legalCompanyLogo)) return null;

  return (
    <Stack direction="row" spacing={-1.5} mt={mt} {...props}>
      {serviceLogo && (
        <CustomTooltip title={serviceName}>
          <span>
            <AssetLogo logo={serviceLogo} name={serviceName} size={size} />
          </span>
        </CustomTooltip>
      )}
      {companyLogo && (
        <CustomTooltip title={companyName}>
          <span>
            <AssetLogo logo={companyLogo} name={companyName} size={size} />
          </span>
        </CustomTooltip>
      )}
      {externalServiceLogo && (
        <CustomTooltip title={externalServiceName}>
          <span>
            <Avatar src={externalServiceLogo} alt={externalServiceName} size={size} transparent sx={{ objectFit: 'contain' }} />
          </span>
        </CustomTooltip>
      )}
      {legalCompanyLogo && (
        <CustomTooltip title={legalCompanyName}>
          <span>
            <AssetLogo logo={legalCompanyLogo} name={legalCompanyName} size={size} />
          </span>
        </CustomTooltip>
      )}
    </Stack>
  );
};

export default ExternalConnectionCompanyLogo;
